// Mirrors Wellnotes/Utilities/DeviceHelper.swift + Wellnotes/Views/OnboardingView.swift
// pairing helpers (deviceID, secret, hash, deviceInfos).

const DEVICE_LINK_ID_KEY = "device_link_id";

const TZ_CITY_TO_COUNTRY = {
  Tirana: "Albania",
  Andorra: "Andorra",
  Vienna: "Austria",
  Minsk: "Belarus",
  Brussels: "Belgium",
  Sarajevo: "Bosnia and Herzegovina",
  Sofia: "Bulgaria",
  Zagreb: "Croatia",
  Nicosia: "Cyprus",
  Prague: "Czech Republic",
  Copenhagen: "Denmark",
  Tallinn: "Estonia",
  Helsinki: "Finland",
  Paris: "France",
  Berlin: "Germany",
  Athens: "Greece",
  Budapest: "Hungary",
  Reykjavik: "Iceland",
  Dublin: "Ireland",
  Rome: "Italy",
  Riga: "Latvia",
  Vaduz: "Liechtenstein",
  Vilnius: "Lithuania",
  Luxembourg: "Luxembourg",
  Valletta: "Malta",
  Chisinau: "Moldova",
  Monaco: "Monaco",
  Podgorica: "Montenegro",
  Amsterdam: "Netherlands",
  Skopje: "North Macedonia",
  Oslo: "Norway",
  Warsaw: "Poland",
  Lisbon: "Portugal",
  Bucharest: "Romania",
  Moscow: "Russia",
  Belgrade: "Serbia",
  Bratislava: "Slovakia",
  Ljubljana: "Slovenia",
  Madrid: "Spain",
  Stockholm: "Sweden",
  Bern: "Switzerland",
  Kyiv: "Ukraine",
  Kiev: "Ukraine",
  London: "United Kingdom",
  Edinburgh: "United Kingdom",
  Belfast: "United Kingdom",
  Vatican: "Vatican City",
  Ankara: "Turkey",
  Pristina: "Kosovo",
  Ottawa: "Canada",
  "Mexico City": "Mexico",
  "Guatemala City": "Guatemala",
  Belmopan: "Belize",
  Tegucigalpa: "Honduras",
  "San Salvador": "El Salvador",
  Managua: "Nicaragua",
  "San Jose": "Costa Rica",
  Panama: "Panama",
  Havana: "Cuba",
  Kingston: "Jamaica",
  Nassau: "Bahamas",
  "Port au Prince": "Haiti",
  "Santo Domingo": "Dominican Republic",
  Bridgetown: "Barbados",
  Castries: "Saint Lucia",
  Kingstown: "Saint Vincent and the Grenadines",
  "Saint Johns": "Antigua and Barbuda",
  Roseau: "Dominica",
  "Saint Georges": "Grenada",
  "Port of Spain": "Trinidad and Tobago",
  Paramaribo: "Suriname",
  Georgetown: "Guyana",
  Caracas: "Venezuela",
  Bogota: "Colombia",
  Quito: "Ecuador",
  Lima: "Peru",
  "La Paz": "Bolivia",
  Sucre: "Bolivia",
  Asuncion: "Paraguay",
  Montevideo: "Uruguay",
  "Buenos Aires": "Argentina",
  Santiago: "Chile",
  Brasilia: "Brazil",
  Washington: "United States",
  Algiers: "Algeria",
  Luanda: "Angola",
  Gaborone: "Botswana",
  Cairo: "Egypt",
  "Addis Ababa": "Ethiopia",
  Nairobi: "Kenya",
  Rabat: "Morocco",
  Pretoria: "South Africa",
  "Cape Town": "South Africa",
  Tunis: "Tunisia",
  Kabul: "Afghanistan",
  Yerevan: "Armenia",
  Baku: "Azerbaijan",
  Dhaka: "Bangladesh",
  Beijing: "China",
  Tbilisi: "Georgia",
  "New Delhi": "India",
  Jakarta: "Indonesia",
  Tehran: "Iran",
  Baghdad: "Iraq",
  Jerusalem: "Israel",
  Tokyo: "Japan",
  Amman: "Jordan",
  "Kuwait City": "Kuwait",
  Beirut: "Lebanon",
  "Kuala Lumpur": "Malaysia",
  Manila: "Philippines",
  Doha: "Qatar",
  Riyadh: "Saudi Arabia",
  Singapore: "Singapore",
  Seoul: "South Korea",
  Taipei: "Taiwan",
  Bangkok: "Thailand",
  "Abu Dhabi": "United Arab Emirates",
  Hanoi: "Vietnam",
  Canberra: "Australia",
  Wellington: "New Zealand",
};

function bufferToHex(buffer) {
  const bytes = new Uint8Array(buffer);
  let hex = "";
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, "0");
  }
  return hex;
}

export function uuidV4() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = bufferToHex(bytes);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function deviceLinkID() {
  const existing = localStorage.getItem(DEVICE_LINK_ID_KEY);
  if (existing && existing.length > 0) return existing;
  const fresh = uuidV4();
  localStorage.setItem(DEVICE_LINK_ID_KEY, fresh);
  return fresh;
}

export function generatePairingSecret() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return bufferToHex(bytes);
}

export async function pairingSecretHash(secret) {
  const data = new TextEncoder().encode(secret);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return bufferToHex(digest);
}

function pairingSecretStorageKey(pairingID) {
  return `pairing_secret_${pairingID}`;
}

export function loadOrCreatePairingSecret(pairingID) {
  const key = pairingSecretStorageKey(pairingID);
  const stored = localStorage.getItem(key);
  if (stored && stored.length > 0) return stored;
  const fresh = generatePairingSecret();
  localStorage.setItem(key, fresh);
  return fresh;
}

export function clearStoredPairingSecret(pairingID) {
  localStorage.removeItem(pairingSecretStorageKey(pairingID));
}

function detectBrowserName(ua) {
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return "Safari";
  if (/OPR\//.test(ua)) return "Opera";
  return "Web";
}

function browserVersion(ua, name) {
  const patterns = {
    Edge: /Edg\/([\d.]+)/,
    Chrome: /Chrome\/([\d.]+)/,
    Firefox: /Firefox\/([\d.]+)/,
    Safari: /Version\/([\d.]+)/,
    Opera: /OPR\/([\d.]+)/,
  };
  const re = patterns[name];
  if (!re) return "";
  const match = ua.match(re);
  return match ? match[1] : "";
}

function fallbackCity() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const parts = tz.split("/");
    const raw = (parts[parts.length - 1] || "").replace(/_/g, " ");
    return raw || "Unknown";
  } catch (_) {
    return "Unknown";
  }
}

function fallbackCountry() {
  try {
    const region = (navigator.language || "").split("-")[1];
    if (!region) return "Unknown";
    try {
      const display = new Intl.DisplayNames([navigator.language || "en"], { type: "region" });
      return display.of(region) || region;
    } catch (_) {
      return region;
    }
  } catch (_) {
    return "Unknown";
  }
}

function countryFromCity(city) {
  return TZ_CITY_TO_COUNTRY[city] || "";
}

export async function deviceInfos(userID) {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const browser = detectBrowserName(ua);
  const version = browserVersion(ua, browser);

  const city = fallbackCity();
  const country = countryFromCity(city) || fallbackCountry();

  // Mirror the iOS deviceInfos shape but expose the browser as the "system":
  // - systemName + systemVersion are shown together (e.g., "Safari 17.4")
  // - deviceName (lowercased) drives icon selection on the iOS scanner
  // - model is shown in the pairing sheet title
  return {
    systemName: browser,
    systemVersion: version,
    deviceName: browser.toLowerCase(),
    model: `${browser}${version ? " " + version : ""}`.trim(),
    city,
    country,
    deviceID: deviceLinkID(),
    userID: userID || "",
  };
}
